import { useState } from 'react'

export interface UseModalIframeType {
  title?: string
  iframeLink: string
  isModalVisible: boolean
  enableModalVisibility: (title: string, iframeLink: string) => void
  disableModalVisibility: () => void
}

/**
 * useModalIframe is a custom React hook that manages the visibility
 * of a modal iframe and stores the target iframe link to be loaded.
 *
 * ### Returned State
 * - `isModalVisible`: Whether the modal is currently visible.
 * - `iframeLink`: The URL to load inside the iframe.
 * - `title`: The title of the modal dialog.
 *
 * ### Returned Functions
 * - `enableModalVisibility(link: string)`: Shows the modal and sets the iframe link.
 * - `disableModalVisibility()`: Hides the modal.
 *
 * ### Example
 * ```tsx
 * const {
 *   isModalVisible,
 *   title,
 *   iframeLink,
 *   enableModalVisibility,
 *   disableModalVisibility
 * } = useModalIframe()
 * ```
 */
export const useModalIframe = (): UseModalIframeType => {
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false)
  const [iframeLink, setIframeLink] = useState<string>('')
  const [title, setTitle] = useState<string>('')

  const enableModalVisibility = (title: string, iframeLink: string) => {
    setIsModalVisible(true)
    setIframeLink(iframeLink)
    setTitle(title)
  }
  const disableModalVisibility = () => setIsModalVisible(false)

  return {
    isModalVisible,
    iframeLink,
    title,
    enableModalVisibility,
    disableModalVisibility,
  }
}
